import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import '@adornis/buchungsheld-components/bh-breadcrumb.js';
import {
  LecturerContract,
  LecturerContractFile,
  LecturerContractVariable,
} from '@adornis/buchungsheld-db/entities/LecturerContract.js';
import { dataviewEmptyState } from '@adornis/buchungsheld-db/global-helpers.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-loading-state';
import '@adornis/chemistry/elements/components/x-section.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import '@adornis/data-view/client/x-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { BHPromptLecturerContractModify } from '../../../../prompts/bh-prompt-lecturer-contract-modify.js';

@customElement('bh-page-admin-einrichtung-settings-contracts')
export class BHPageAdminEinrichtungSettingsContracts extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID?: string;

  override render() {
    const institutionID = this.institutionID;
    if (!institutionID) return html`<x-loading-state></x-loading-state>`;

    return html`
      <x-flex space="xl">
        <bh-breadcrumb
          .items=${[
            {
              content: this._translation.translate('settings', 'name'),
              href: `/admin/institution-details/${this.institutionID}/settings`,
            },
            { content: this._translation.translate('settings', 'manage_contracts'), active: true },
          ]}
          .additionalAction=${html`
            <tr-button-small
              icon="add"
              .action=${() => BHPromptLecturerContractModify.add(institutionID)}
            ></tr-button-small>
          `}
        ></bh-breadcrumb>
        <tr-flex
          ${css({
            background: '#F3F2F2',
            borderRadius: '5px',
            padding: this.spacing.lg,
            marginBottom: this.spacing.md,
          })}
          space="md"
        >
          <tr-text bold> Erklärung: </tr-text>
          <tr-text> ${this._translation.translate('fieldInfo', 'contract', 'description')} </tr-text>
          <hr />
          <tr-text bold> Verfügbare Variablen: </tr-text>
          <ul ${css({ margin: '0' })}>
            ${Object.keys(LecturerContractVariable).map(key => html` <li><tr-text>${key}</tr-text></li> `)}
          </ul>
        </tr-flex>
        <x-data-view
          .options=${{
            class: LecturerContract,
            emptyState: () => dataviewEmptyState({}),
            selectionSet: { file: AdornisFile.allFields, title: 1, createdAt: 1, fileID: 1, text: 1 },
            view: new View({
              filter: new AdornisFilter({ searchFields: ['institutionID'], search: this.institutionID }),
            }),
            customColumns: [
              { fieldName: 'title' },
              {
                columnName: 'Datei',
                fieldName: 'file',
                selectionSet: { ...AdornisFile.allFields, fileID: 1, file: AdornisFile.allFields },
                renderItem: instance => {
                  const text = instance.file
                    ? instance.file.meta?.fileName
                    : this._translation.translate('settings', 'no_file');
                  return html` <x-text> ${text} </x-text> `;
                },
              },
              {
                fieldName: 'createdAt',
                columnWidth: 'max-content',
                getFieldMapping: instance => instance.createdAt.toLocaleString(DateTime.DATE_MED),
              },
              {
                columnName: '',
                customID: '_options',
                selectionSet: { fileID: 1, file: { meta: { fileName: 1, extension: 1 } } },
                renderItem: instance => {
                  return html`
                    <x-flex space="xs" wrap horizontal>
                      <tr-button-small
                        icon="edit"
                        .color=${this.colors.secondary.shade(300)}
                        .action=${() => BHPromptLecturerContractModify.edit(institutionID, instance)}
                      ></tr-button-small>
                      <tr-button-small
                        icon="download"
                        .color=${this.colors.secondary.shade(300)}
                        @click=${() =>
                          goTo(
                            `${LecturerContractFile.getServeLink(instance.fileID)}/${
                              instance.file?.meta?.completeName
                            }`,
                            { target: '_blank' },
                          )}
                      ></tr-button-small>
                      <tr-button-small
                        icon="delete"
                        .color=${this.colors.tone.error}
                        .action=${async () => {
                          if (!(await XDialog.confirm(this._translation.translate('settings', 'del_crt_cnf')))) return;

                          const fileID = instance.fileID;

                          await AdornisFile.removeEntity(AdornisFile._class, fileID)();
                          await instance.remove();
                        }}
                      ></tr-button-small>
                    </x-flex>
                  `;
                },
                columnWidth: 'max-content',
              },
            ] as Array<ColumnDefinition<typeof LecturerContract>>,
          } satisfies DataViewOptions<typeof LecturerContract>}
        ></x-data-view>
      </x-flex>
    `;
  }
}
